import React from 'react';
import { Link } from 'react-router-dom';
import { MailOutlined, LinkedinOutlined } from '@ant-design/icons';
import kiplerLogo from '../logo_white.svg'; // Assuming white version exists

const ModernFooter = () => {
  const currentYear = new Date().getFullYear();

  const styles = {
    footer: {
      background: 'linear-gradient(135deg, #1a0d14 0%, #230518 50%, #2d0b1f 100%)',
      color: '#ffffff',
      position: 'relative',
      overflow: 'hidden',
      padding: 20,
    },
    footerBorder: {
      content: '',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '1px',
    },
    footerContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px',
    },
    footerContent: {
      display: 'grid',
      gridTemplateColumns: '1.5fr repeat(4, 1fr)',
      gap: '50px',
      padding: '60px 0 40px',
    },
    footerContentMobile: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '30px',
      padding: '40px 0 30px',
    },
    footerContentMobileFirst: {
      gridColumn: '1 / -1',
      marginBottom: '20px',
    },
    footerLogo: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      marginBottom: '16px',
    },
    logoImage: {
      height: '40px',
      width: 'auto',
    },
    companyDescription: {
      fontSize: '14px',
      lineHeight: '1.6',
      color: '#b8b8b8',
      marginBottom: '24px',
      maxWidth: '280px',
    },
    socialLinks: {
      display: 'flex',
      gap: '12px',
    },
    socialLink: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      borderRadius: '8px',
      background: 'rgba(255, 255, 255, 0.1)',
      color: '#ffffff',
      textDecoration: 'none',
      transition: 'all 0.3s ease',
      fontSize: '16px',
    },
    sectionTitle: {
      fontSize: '16px',
      fontWeight: '600',
      marginBottom: '20px',
      color: '#ffffff',
      letterSpacing: '0.5px',
    },
    footerLinks: {
      listStyle: 'none',
      padding: '0',
      margin: '0',
    },
    footerLinkItem: {
      marginBottom: '12px',
    },
    footerLink: {
      color: '#b8b8b8',
      textDecoration: 'none',
      fontSize: '14px',
      transition: 'color 0.3s ease',
      display: 'inline-block',
      cursor: 'pointer',
    },
    contactInfo: {
      marginBottom: '24px',
    },
    contactItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '12px',
    },
    contactIcon: {
      fontSize: '14px',
      color: '#b8b8b8',
    },
    contactLink: {
      color: '#b8b8b8',
      textDecoration: 'none',
      fontSize: '14px',
      transition: 'color 0.3s ease',
    },
    securityBadges: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
    },
    badge: {
      background: 'rgba(255, 255, 255, 0.1)',
      padding: '6px 12px',
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
    },
    badgeText: {
      fontSize: '12px',
      fontWeight: '500',
    },
    footerBottom: {
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      padding: '24px 0',
    },
    footerBottomContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '16px',
    },
    footerBottomContentMobile: {
      flexDirection: 'column',
      textAlign: 'center',
    },
    copyright: {
      margin: '0',
      fontSize: '14px',
      color: '#b8b8b8',
    },
    footerBottomLinks: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '14px',
      color: '#b8b8b8',
    },
    separator: {
      opacity: '0.5',
    },
  };

  // Check if screen is mobile size
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const footerContentStyle = isMobile ? styles.footerContentMobile : styles.footerContent;
  const footerBottomContentStyle = isMobile ? 
    {...styles.footerBottomContent, ...styles.footerBottomContentMobile} : 
    styles.footerBottomContent;

  return (
    <footer style={styles.footer}>
      <div style={styles.footerBorder}></div>
      <div style={styles.footerContainer}>
        {/* Main Footer Content */}
        <div style={footerContentStyle}>
          {/* Company Info Section */}
          <div style={isMobile ? styles.footerContentMobileFirst : {}}>
            <div style={styles.footerLogo}>
              <img src={kiplerLogo} alt="Kipler Logo" style={styles.logoImage} />
            </div>
            <p style={styles.companyDescription}>
              AI-drevet transkribering og analyse for psykologer. 
              Mer tid til pasientene, mindre tid til papirarbeid.
            </p>
            <div style={styles.socialLinks}>
              <a 
                href="https://www.linkedin.com/company/kipler/" 
                style={styles.socialLink} 
                aria-label="LinkedIn"
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <LinkedinOutlined />
              </a>
              <a 
                href="mailto:post@kipler.ai" 
                style={styles.socialLink} 
                aria-label="Email"
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <MailOutlined />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={styles.sectionTitle}>Produktet</h4>
            <ul style={styles.footerLinks}>
              <li style={styles.footerLinkItem}>
                <Link 
                  to="/" 
                  style={styles.footerLink}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#ffffff';
                    e.target.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#b8b8b8';
                    e.target.style.transform = 'translateX(0)';
                  }}
                >
                  Hjem
                </Link>
              </li>
              <li style={styles.footerLinkItem}>
                <Link 
                  to="/about" 
                  style={styles.footerLink}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#ffffff';
                    e.target.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#b8b8b8';
                    e.target.style.transform = 'translateX(0)';
                  }}
                >
                  Om oss
                </Link>
              </li>
              <li style={styles.footerLinkItem}>
                <a 
                  href="#pris" 
                  style={styles.footerLink}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#ffffff';
                    e.target.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#b8b8b8';
                    e.target.style.transform = 'translateX(0)';
                  }}
                >
                  Priser
                </a>
              </li>
              <li style={styles.footerLinkItem}>
                <Link 
                  to="/blog" 
                  style={styles.footerLink}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#ffffff';
                    e.target.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#b8b8b8';
                    e.target.style.transform = 'translateX(0)';
                  }}
                >
                  Blogg
                </Link>
              </li>
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 style={styles.sectionTitle}>Support</h4>
            <ul style={styles.footerLinks}>
              <li style={styles.footerLinkItem}>
                <Link 
                  to="/clients" 
                  style={styles.footerLink}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#ffffff';
                    e.target.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#b8b8b8';
                    e.target.style.transform = 'translateX(0)';
                  }}
                >
                  For klienter
                </Link>
              </li>
              <li style={styles.footerLinkItem}>
                <p 
                  style={styles.footerLink}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#ffffff';
                    e.target.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#b8b8b8';
                    e.target.style.transform = 'translateX(0)';
                  }}
                  onClick={() => {
                    if (window.tidioChatApi) {
                      window.tidioChatApi.open();
                    } else {
                      console.warn("Tidio-chat er ikke lastet inn.");
                    }
                  }}
                >
                  Chat med oss
                </p>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 style={styles.sectionTitle}>Juridisk</h4>
            <ul style={styles.footerLinks}>
              <li style={styles.footerLinkItem}>
                <Link 
                  to="/privacy" 
                  style={styles.footerLink}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#ffffff';
                    e.target.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#b8b8b8';
                    e.target.style.transform = 'translateX(0)';
                  }}
                >
                  Personvernerklæring
                </Link>
              </li>
              <li style={styles.footerLinkItem}>
                <Link 
                  to="/cookie" 
                  style={styles.footerLink}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#ffffff';
                    e.target.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#b8b8b8';
                    e.target.style.transform = 'translateX(0)';
                  }}
                >
                  Informasjonskapsler
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 style={styles.sectionTitle}>Kontakt</h4>
            <div style={styles.contactInfo}>
              <div style={styles.contactItem}>
                <MailOutlined style={styles.contactIcon} />
                <a 
                  href="mailto:post@kipler.ai" 
                  style={styles.contactLink}
                  onMouseEnter={(e) => e.target.style.color = '#ffffff'}
                  onMouseLeave={(e) => e.target.style.color = '#b8b8b8'}
                >
                  post@kipler.ai
                </a>
              </div>
            </div>
            <div style={styles.securityBadges}>
              <div style={styles.badge}>
                <span style={styles.badgeText}>🔒 I henhold til GDPR</span>
              </div>
              <div style={styles.badge}>
                <span style={styles.badgeText}>🛡️ Sikker lagring i Norge</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div style={styles.footerBottom}>
          <div style={footerBottomContentStyle}>
            <p style={styles.copyright}>
              © {currentYear} Kipler AS. Alle rettigheter forbeholdt.
            </p>
            <div style={styles.footerBottomLinks}>
              <span>Org.nr: 934 936 949 MVA</span>
              <span style={styles.separator}>•</span>
              <span>Oslo, Norge</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ModernFooter;